import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../../components/feature/AdminLayout';
import {
  createRecruitmentJob,
  fetchRecruitmentJob,
  updateRecruitmentJob,
  type JobPayload,
  type RecruitmentJobStatus,
} from '../../../api/recruitment.api';

function lines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function RecruitmentJobFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id && id !== 'new');
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [department, setDepartment] = useState('');
  const [employmentType, setEmploymentType] = useState('full_time');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [responsibilities, setResponsibilities] = useState('');
  const [requirements, setRequirements] = useState('');
  const [niceToHave, setNiceToHave] = useState('');
  const [benefits, setBenefits] = useState('');
  const [skills, setSkills] = useState('');
  const [status, setStatus] = useState<RecruitmentJobStatus>('draft');

  useEffect(() => {
    if (!editing) return;
    void (async () => {
      const r = await fetchRecruitmentJob(Number(id));
      setLoading(false);
      if (r.ok === false) {
        if (r.unauthorized) navigate('/admin/login', { replace: true });
        else setError(r.message);
        return;
      }
      setTitle(r.data.title);
      setSlug(r.data.slug);
      setDepartment(r.data.department);
      setEmploymentType(r.data.employment_type);
      setLocation(r.data.location);
      setDescription(r.data.description ?? '');
      setResponsibilities((r.data.responsibilities ?? []).join('\n'));
      setRequirements((r.data.requirements ?? []).join('\n'));
      setNiceToHave((r.data.nice_to_have ?? []).join('\n'));
      setBenefits((r.data.benefits ?? []).join('\n'));
      setSkills((r.data.skills ?? []).join('\n'));
      setStatus(r.data.status);
    })();
  }, [editing, id, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload: JobPayload = {
      title,
      slug,
      department,
      employment_type: employmentType,
      location,
      description,
      responsibilities: lines(responsibilities),
      requirements: lines(requirements),
      nice_to_have: lines(niceToHave),
      benefits: lines(benefits),
      skills: lines(skills),
      status,
    };
    const r = editing ? await updateRecruitmentJob(Number(id), payload) : await createRecruitmentJob(payload);
    setSaving(false);
    if (r.ok === false) {
      if (r.unauthorized) navigate('/admin/login', { replace: true });
      else setError(r.message);
      return;
    }
    navigate('/recruitment/jobs');
  };

  const fieldClass = 'w-full rounded-xl border border-gray-200 px-3 py-2 text-sm';

  return (
    <AdminLayout>
      <form onSubmit={(e) => void onSubmit(e)} className="max-w-3xl flex flex-col gap-5">
        <div>
          <h1 className="text-xl font-black text-gray-900">
            {editing ? t('recruitment.jobs.edit') : t('recruitment.jobs.add')}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('page.recruitment_jobs.subtitle')}</p>
        </div>

        {loading ? <p className="text-sm text-gray-400">{t('recruitment.loading')}</p> : null}
        {error ? <div className="rounded-xl bg-rose-50 text-rose-700 text-sm px-3 py-2">{error}</div> : null}

        <div className="rounded-2xl bg-white ring-1 ring-gray-100 p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="md:col-span-2 text-xs font-bold text-gray-500">
            {t('recruitment.jobs.fields.title')}
            <input required value={title} onChange={(e) => setTitle(e.target.value)} className={`${fieldClass} mt-1`} />
          </label>
          <label className="text-xs font-bold text-gray-500">
            {t('recruitment.jobs.fields.slug')}
            <input value={slug} onChange={(e) => setSlug(e.target.value)} className={`${fieldClass} mt-1`} dir="ltr" />
          </label>
          <label className="text-xs font-bold text-gray-500">
            {t('recruitment.jobs.fields.department')}
            <input required value={department} onChange={(e) => setDepartment(e.target.value)} className={`${fieldClass} mt-1`} />
          </label>
          <label className="text-xs font-bold text-gray-500">
            {t('recruitment.jobs.fields.employment_type')}
            <select value={employmentType} onChange={(e) => setEmploymentType(e.target.value)} className={`${fieldClass} mt-1 bg-white`}>
              <option value="full_time">{t('recruitment.employment.full_time')}</option>
              <option value="part_time">{t('recruitment.employment.part_time')}</option>
              <option value="contract">{t('recruitment.employment.contract')}</option>
              <option value="internship">{t('recruitment.employment.internship')}</option>
            </select>
          </label>
          <label className="text-xs font-bold text-gray-500">
            {t('recruitment.jobs.fields.location')}
            <input required value={location} onChange={(e) => setLocation(e.target.value)} className={`${fieldClass} mt-1`} />
          </label>
          <label className="text-xs font-bold text-gray-500">
            {t('recruitment.jobs.fields.status')}
            <select value={status} onChange={(e) => setStatus(e.target.value as RecruitmentJobStatus)} className={`${fieldClass} mt-1 bg-white`}>
              {(['draft', 'published', 'closed', 'archived'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`recruitment.job_status.${s}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="md:col-span-2 text-xs font-bold text-gray-500">
            {t('recruitment.jobs.fields.description')}
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className={`${fieldClass} mt-1`} />
          </label>
          {[
            ['responsibilities', responsibilities, setResponsibilities],
            ['requirements', requirements, setRequirements],
            ['nice_to_have', niceToHave, setNiceToHave],
            ['benefits', benefits, setBenefits],
            ['skills', skills, setSkills],
          ].map(([key, value, setter]) => (
            <label key={String(key)} className="md:col-span-2 text-xs font-bold text-gray-500">
              {t(`recruitment.jobs.fields.${key}`)}
              <textarea
                value={value as string}
                onChange={(e) => (setter as (v: string) => void)(e.target.value)}
                rows={4}
                className={`${fieldClass} mt-1`}
                placeholder={t('recruitment.jobs.fields.list_hint')}
              />
            </label>
          ))}
        </div>

        <div className="flex gap-2">
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl bg-teal-600 text-white text-xs font-bold disabled:opacity-60">
            {saving ? t('recruitment.saving') : t('recruitment.save')}
          </button>
          <button type="button" onClick={() => navigate('/recruitment/jobs')} className="px-4 py-2 rounded-xl bg-white ring-1 ring-gray-200 text-xs font-bold">
            {t('recruitment.cancel')}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}
